import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, Clock, AlertTriangle, Calendar, Briefcase, 
  ChevronRight, Target, FileText, MessageSquare, Bell,
  ThumbsUp, ThumbsDown, Edit3, Upload, User, Loader2,
  TrendingUp, Users, Star, Zap
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const AECommandCenter = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (userEmail) {
      fetchDashboard();
    }
  }, [userEmail]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/dashboard/client?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setDashboard(data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (contentId) => {
    setActionLoading(contentId);
    try {
      await fetch(`${API}/api/always-elevate/content/${contentId}/approve?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve', comment: '' })
      });
      fetchDashboard();
    } catch (err) {
      console.error('Error approving:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestChanges = async (contentId) => {
    setActionLoading(contentId);
    try {
      await fetch(`${API}/api/always-elevate/content/${contentId}/approve?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request_changes', comment: 'Needs revision' })
      });
      fetchDashboard();
    } catch (err) {
      console.error('Error requesting changes:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  const { approvals = [], tasks = [], upcoming = [], partnerships = [], brand_focus, stats = {} } = dashboard || {};

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6" data-testid="ae-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>
                Welcome back, Akayleb
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors relative">
                <Bell className="w-5 h-5" />
                {(approvals.length + tasks.length) > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C4A052] rounded-full text-xs flex items-center justify-center text-black font-bold">
                    {approvals.length + tasks.length}
                  </span>
                )}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#C4A052]/20 text-[#C4A052] rounded-lg hover:bg-[#C4A052]/30 transition-colors">
                <MessageSquare className="w-4 h-4" />
                Message Team
              </button>
            </div>
          </div>
          
          {/* Brand Focus Banner */}
          {brand_focus && (
            <div className="mt-4 p-4 bg-gradient-to-r from-[#C4A052]/10 to-transparent border border-[#C4A052]/20 rounded-xl">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Current Theme</p>
                  <p className="text-lg font-semibold text-[#C4A052]">{brand_focus.monthly_theme}</p>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Campaign Phase</p>
                  <p className="text-sm text-white">{brand_focus.campaign_phase}</p>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Priority</p>
                  <p className="text-sm text-white">{brand_focus.priority_pillar}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.content_this_month || 0}</p>
                <p className="text-xs text-gray-400">Content This Month</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#C4A052]/20">
                <Clock className="w-5 h-5 text-[#C4A052]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending_approvals || 0}</p>
                <p className="text-xs text-gray-400">Pending Approvals</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Briefcase className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_partnerships || 0}</p>
                <p className="text-xs text-gray-400">Active Partnerships</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.upcoming_appearances || 0}</p>
                <p className="text-xs text-gray-400">Upcoming Appearances</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Awaiting Your Approval */}
          <div className="lg:col-span-2 bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="approvals-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#C4A052]/20">
                  <CheckCircle2 className="w-5 h-5 text-[#C4A052]" />
                </div>
                <h2 className="font-semibold">Awaiting Your Approval</h2>
              </div>
              <span className="text-xs px-2 py-1 bg-[#C4A052]/20 text-[#C4A052] rounded-full">
                {approvals.length} items
              </span>
            </div>
            <div className="divide-y divide-white/5">
              {approvals.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>All caught up! No items awaiting approval.</p>
                </div>
              ) : (
                approvals.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-white/5 transition-colors" data-testid={`approval-${item.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 capitalize">
                            {item.platform}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 capitalize">
                            {item.series?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <h3 className="font-medium mb-1">{item.title}</h3>
                        <p className="text-sm text-gray-400 line-clamp-2">{item.caption}</p>
                        {item.scheduled_date && (
                          <p className="text-xs text-gray-500 mt-2">
                            Scheduled: {new Date(item.scheduled_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleApprove(item.id)}
                          disabled={actionLoading === item.id}
                          className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors disabled:opacity-50"
                          data-testid={`approve-btn-${item.id}`}
                        >
                          {actionLoading === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                        </button>
                        <button 
                          onClick={() => handleRequestChanges(item.id)}
                          disabled={actionLoading === item.id}
                          className="p-2 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors disabled:opacity-50"
                          data-testid={`changes-btn-${item.id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Requests From Your Team */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="tasks-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <h2 className="font-semibold">Requests From Team</h2>
              </div>
              <span className="text-xs px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full">
                {tasks.length} items
              </span>
            </div>
            <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
              {tasks.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending requests</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="p-4 hover:bg-white/5 transition-colors" data-testid={`task-${task.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        task.task_type === 'upload' ? 'bg-blue-500/20' :
                        task.task_type === 'approval' ? 'bg-green-500/20' :
                        task.task_type === 'availability' ? 'bg-purple-500/20' :
                        'bg-gray-500/20'
                      }`}>
                        {task.task_type === 'upload' ? <Upload className="w-4 h-4 text-blue-400" /> :
                         task.task_type === 'approval' ? <CheckCircle2 className="w-4 h-4 text-green-400" /> :
                         task.task_type === 'availability' ? <Calendar className="w-4 h-4 text-purple-400" /> :
                         <FileText className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{task.title}</h4>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{task.description}</p>
                        {task.due_date && (
                          <p className={`text-xs mt-2 ${
                            new Date(task.due_date) < new Date() ? 'text-red-400' : 'text-gray-500'
                          }`}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                        task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Upcoming & Partnerships Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Upcoming */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="upcoming-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-semibold">Upcoming</h2>
              </div>
              <a href="/ae/calendar" className="text-sm text-[#C4A052] hover:underline">View All</a>
            </div>
            <div className="divide-y divide-white/5">
              {upcoming.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No upcoming events</p>
                </div>
              ) : (
                upcoming.slice(0, 5).map((event) => (
                  <div key={event.id} className="p-4 hover:bg-white/5 transition-colors flex items-center gap-4">
                    <div className="text-center min-w-[50px]">
                      <p className="text-xs text-gray-400 uppercase">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                      <p className="text-xl font-bold text-[#C4A052]">
                        {new Date(event.date).getDate()}
                      </p>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{event.title}</h4>
                      <p className="text-xs text-gray-400">{event.location || 'TBD'}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      event.event_type === 'shoot' ? 'bg-blue-500/20 text-blue-400' :
                      event.event_type === 'interview' ? 'bg-green-500/20 text-green-400' :
                      event.event_type === 'community' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {event.event_type}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Partnership Opportunities */}
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="partnerships-section">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="font-semibold">Partnership Opportunities</h2>
              </div>
              <a href="/ae/partnerships" className="text-sm text-[#C4A052] hover:underline">View All</a>
            </div>
            <div className="divide-y divide-white/5">
              {partnerships.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending opportunities</p>
                </div>
              ) : (
                partnerships.map((partner) => (
                  <div key={partner.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium">{partner.brand_name}</h4>
                        <p className="text-sm text-gray-400">{partner.partnership_type} • {partner.industry}</p>
                        {partner.elle_recommendation && (
                          <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {partner.elle_recommendation}
                          </p>
                        )}
                        {partner.deadline && (
                          <p className="text-xs text-gray-500 mt-1">
                            Decision by: {new Date(partner.deadline).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <button className="px-3 py-1.5 bg-[#C4A052]/20 text-[#C4A052] rounded-lg text-sm hover:bg-[#C4A052]/30 transition-colors">
                        Review
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AECommandCenter;
